import { endOfMonth, format, startOfMonth } from 'date-fns';
import { History } from 'history';
import { padStart } from 'lodash';
import type {
  AccountType,
  FinancialInstitutions
} from '@melio/payments-recon-api-client-ts';
import { RECORD_PANEL_QUERY_PARAM_TRIGGER } from '../consts/reco.const';
import { reconEnumValue } from './reconEnumValue';

export function parseQuickAccessQuery(query: string, history: History) {
  const keywords = new Set(query.trim().toLowerCase().split(/\s/));

  const GAPS = keywords.has('gaps') || keywords.has('gap');
  const REPORT = keywords.has('rep') || keywords.has('report');
  const VENDOR_BALANCE = keywords.has('bal') || keywords.has('balance');
  const DDA = keywords.has('dda');
  const FBO = keywords.has('fbo') || keywords.has('fvo');
  const MSB = keywords.has('msb') || keywords.has('mtl');
  const ACH = keywords.has('ach');
  const CHECKS = keywords.has('check') || keywords.has('checks');
  const CARDS = keywords.has('card') || keywords.has('cards');
  const JPM = keywords.has('jpm');
  const SVB = keywords.has('svb');
  const EVOLVE = keywords.has('evolve');

  if (GAPS) {
    return parseGapsQuery(keywords, history);
  }

  if (REPORT) {
    return parseReportQuery(keywords, history);
  }

  if (VENDOR_BALANCE) {
    return parseDailyBalanceQuery(keywords, history);
  }

  if (DDA) {
    return parseRevenueAccountsQuery(keywords, history);
  }

  if (FBO || MSB) {
    return parseCustomerFundsQuery(keywords, history);
  }

  if (JPM || SVB || EVOLVE) {
    return parseRevenueAccountsQuery(keywords, history);
  }

  if (ACH || CHECKS || CARDS) {
    return parseCustomerFundsQuery(keywords, history);
  }

  return parseRecordLookupQuery(keywords, history);
}

function parseGapsQuery(keywords: Set<string>, history: History) {
  const MSB = keywords.has('msb') || keywords.has('mtl');
  const SVB = keywords.has('svb');
  const EVOLVE = keywords.has('evolve');
  const YEAR =
    Array.from(keywords).find((keyword) => keyword.match(/\d{4}/)) ||
    new Date().getFullYear();

  const accountType = MSB
    ? reconEnumValue<AccountType>('msb')
    : reconEnumValue<AccountType>('fbo');
  let bank = reconEnumValue<FinancialInstitutions>('jpm');
  if (EVOLVE) bank = reconEnumValue<FinancialInstitutions>('evolve');
  if (SVB) bank = reconEnumValue<FinancialInstitutions>('svb');

  history.push({
    pathname: `/reco/gaps/${YEAR}`,
    search: `accountType=${accountType}&bank=${bank}`
  });
}

function parseReportQuery(keywords: Set<string>, history: History) {
  const dateRange = getDateRange(keywords);

  history.push({
    pathname: `/reco/jpm-report/daily`,
    search: `${dateRange}`
  });
}

function parseDailyBalanceQuery(keywords: Set<string>, history: History) {
  const SVB = keywords.has('svb');
  const EVOLVE = keywords.has('evolve');
  const ALERTED =
    keywords.has('a') ||
    keywords.has('al') ||
    keywords.has('alert') ||
    keywords.has('alerted');
  const MANUAL =
    keywords.has('m') || keywords.has('man') || keywords.has('manual');

  let bank = reconEnumValue<FinancialInstitutions>('jpm');
  if (EVOLVE) bank = reconEnumValue<FinancialInstitutions>('evolve');
  if (SVB) bank = reconEnumValue<FinancialInstitutions>('svb');

  let path = 'review';
  if (ALERTED) path = 'alerted';
  if (MANUAL) path = 'manual';

  history.push({
    pathname: `/reco/vendorBalance/vendors/${path}`,
    search: `financialInstitution=${bank}`
  });
}

function parseRevenueAccountsQuery(keywords: Set<string>, history: History) {
  const MSB = keywords.has('msb') || keywords.has('mtl');
  const SVB = keywords.has('svb');
  const EVOLVE = keywords.has('evolve');
  const PENDING =
    keywords.has('pen') || keywords.has('pend') || keywords.has('pending');
  const COMPLETED =
    keywords.has('com') ||
    keywords.has('comp') ||
    keywords.has('complete') ||
    keywords.has('completed');

  const accountType = MSB
    ? reconEnumValue<AccountType>('msb-dda')
    : reconEnumValue<AccountType>('dda');

  let bank = reconEnumValue<FinancialInstitutions>('jpm');
  if (EVOLVE) bank = reconEnumValue<FinancialInstitutions>('evolve');
  if (SVB) bank = reconEnumValue<FinancialInstitutions>('svb');

  let reconciliationStatus = 'review';
  if (PENDING) reconciliationStatus = 'pendingCorrectionEntry';
  if (COMPLETED) reconciliationStatus = 'complete';

  const dateRange = getDateRange(keywords);

  history.push({
    pathname: `/reco/revenue-accounts`,
    search: `accountType=${accountType}${dateRange}&financialInstitution=${bank}&reconciliationStatus=${reconciliationStatus}`
  });
}

function parseCustomerFundsQuery(keywords: Set<string>, history: History) {
  const MSB = keywords.has('msb') || keywords.has('mtl');
  const CHECKS = keywords.has('check') || keywords.has('checks');
  const CARDS = keywords.has('card') || keywords.has('cards');
  const PENDING =
    keywords.has('pen') || keywords.has('pend') || keywords.has('pending');
  const COMPLETED =
    keywords.has('com') ||
    keywords.has('comp') ||
    keywords.has('complete') ||
    keywords.has('completed');

  let reconciliationStatus = 'review';
  if (PENDING) reconciliationStatus = 'pendingCorrectionEntry';
  if (COMPLETED) reconciliationStatus = 'complete';

  const accountType = MSB
    ? reconEnumValue<AccountType>('msb')
    : reconEnumValue<AccountType>('fbo');
  let paymentMethod = 'ach';
  if (CARDS) paymentMethod = 'card';
  if (CHECKS) paymentMethod = 'check';

  const dateRange = getDateRange(keywords);

  history.push({
    pathname: `/reco/customer-funds`,
    search: `paymentMethod=${paymentMethod}&accountType=${accountType}${dateRange}&reconciliationStatus=${reconciliationStatus}`
  });
}

function parseRecordLookupQuery(keywords: Set<string>, history: History) {
  const RECO_ID_LENGTH = 32;
  const TRANSACTION_ID_LENGTH = 22;
  const REFERENCE_ID_LENGTH = 14;
  const MAX_PAYMENT_ID_LENGTH = 10;

  const AMBIGUOUS_RECORD_ID = Array.from(keywords).find(
    (keyword) =>
      keyword.length === RECO_ID_LENGTH ||
      keyword.length === TRANSACTION_ID_LENGTH ||
      keyword.length === REFERENCE_ID_LENGTH ||
      (keyword.match(/^\d+$/) && keyword.length <= MAX_PAYMENT_ID_LENGTH)
  );

  if (AMBIGUOUS_RECORD_ID) {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set(RECORD_PANEL_QUERY_PARAM_TRIGGER, AMBIGUOUS_RECORD_ID);
    history.push({ search: queryParams.toString() });
  }
}

function getDateRange(keywords: Set<string>) {
  const DATE = Array.from(keywords)
    .find((keyword) => keyword.match(/^\d{1,2}\/\d{2}|\d{2}$/))
    ?.split('/');

  if (!DATE) return '';

  const [month, year] = DATE;

  const from = format(
    startOfMonth(
      new Date(`${padStart(year, 4, '20')}-${padStart(month, 2, '0')}-01`)
    ),
    'yyyy-MM-dd'
  );

  const to = format(
    endOfMonth(
      new Date(`${padStart(year, 4, '20')}-${padStart(month, 2, '0')}-01`)
    ),
    'yyyy-MM-dd'
  );

  return `&from=${from}&to=${to}`;
}
